import * as React from 'react';
import { Button, Label } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { aggregate } from '../../../../shared/riskregister/aggregate';
import { filteredRows, trendRows } from '../../../../shared/riskregister/filters';
import { groupLabel, isGapUnit, unitValue } from '../../../../shared/riskregister/grouping';
import { netInWindow } from '../../../../shared/riskregister/trend';
import type { GroupDimension, Payload, Row, ViewState } from '../../../../shared/riskregister/types';

interface AccountabilityGroup {
  unit: string;
  rows: Row[];
}

const ACCOUNTABILITY_LABELS: Record<GroupDimension, string> = {
  project: 'Project',
  cat: 'Risk category',
  epic: 'Initiative',
  assignee: 'Owner',
  svp: 'Platform area',
  manager: 'Team / manager',
  app: 'Application',
};

function groupsFor(rows: Row[], dimension: GroupDimension, payload: Payload): AccountabilityGroup[] {
  const grouped = new Map<string, Row[]>();
  rows.forEach((row) => {
    const unit = unitValue(row, dimension, payload);
    const group = grouped.get(unit);
    if (group) group.push(row);
    else grouped.set(unit, [row]);
  });

  return Array.from(grouped, ([unit, groupRows]) => ({ unit, rows: groupRows })).sort((left, right) => {
    const leftIsGap = dimension === 'manager' && isGapUnit('manager', left.unit, payload);
    const rightIsGap = dimension === 'manager' && isGapUnit('manager', right.unit, payload);
    if (leftIsGap !== rightIsGap) return Number(leftIsGap) - Number(rightIsGap);
    const leftAggregate = aggregate(left.rows, payload);
    const rightAggregate = aggregate(right.rows, payload);
    return (
      rightAggregate.overdue - leftAggregate.overdue ||
      rightAggregate.weighted - leftAggregate.weighted ||
      rightAggregate.maxidle - leftAggregate.maxidle ||
      left.unit.localeCompare(right.unit)
    );
  });
}

export const AccountabilityTable: React.FunctionComponent<{
  payload: Payload;
  state: ViewState;
  onChange: (change: Partial<ViewState>) => void;
}> = ({ payload, state, onChange }) => {
  // groupsFor's comparator calls aggregate() on both sides inside sort(), so an unmemoized render
  // redid the whole grouping and aggregation pass on every keystroke in the search box.
  const rows = React.useMemo(() => filteredRows(payload.rows, state, payload), [payload, state]);
  const groups = React.useMemo(() => groupsFor(rows, state.acct, payload), [rows, state.acct, payload]);
  const missingManagers = rows.filter((row) => row.open && !row.mgr).length;
  const unattributedApps = rows.filter((row) => row.open && !row.app).length;

  if (!rows.length) {
    return <p className="risk-register__empty">No items match the current accountability view.</p>;
  }

  return (
    <>
      <div className="risk-register__table-toolbar">
        <span>
          Holding to account by <strong>{ACCOUNTABILITY_LABELS[state.acct]}</strong>: {groups.length} unit
          {groups.length === 1 ? '' : 's'}.
        </span>
        {state.unit && (
          <Button
            variant="link"
            isInline
            onClick={() => onChange({ unit: '' })}
            aria-label="Clear accountability drill-down"
          >
            × Clear drill-down
          </Button>
        )}
      </div>
      <div className="risk-register__table-wrap">
        <Table aria-label="Accountability by unit" variant="compact">
          <Thead>
            <Tr>
              <Th scope="col">{ACCOUNTABILITY_LABELS[state.acct]}</Th>
              <Th scope="col">Open</Th>
              <Th scope="col">Overdue</Th>
              <Th scope="col">At risk</Th>
              <Th scope="col">Idle</Th>
              <Th scope="col">Weighted risk</Th>
              <Th scope="col">Trend ({state.win})</Th>
            </Tr>
          </Thead>
          <Tbody>
            {groups.map((group) => {
              const summary = aggregate(group.rows, payload);
              const trend = netInWindow(
                trendRows(payload.rows, { ...state, unit: `${state.acct}:${group.unit}` }, payload),
                state.win,
                payload.as_of,
              );
              const label = groupLabel(group.unit, state.acct, payload);
              return (
                <Tr key={group.unit}>
                  <Td dataLabel={ACCOUNTABILITY_LABELS[state.acct]}>
                    <Button
                      variant="link"
                      isInline
                      onClick={() => onChange({ unit: `${state.acct}:${group.unit}` })}
                      aria-label={`Show all Risk Register sections for ${label}`}
                    >
                      {label}
                    </Button>
                  </Td>
                  <Td dataLabel="Open">{summary.open}</Td>
                  <Td dataLabel="Overdue">{summary.overdue}</Td>
                  <Td dataLabel="At risk">{summary.atrisk}</Td>
                  <Td dataLabel="Idle">{summary.stale}</Td>
                  <Td dataLabel="Weighted risk">{summary.weighted}</Td>
                  <Td dataLabel={`Trend (${state.win})`}>
                    <Label color={trend.net > 0 ? 'red' : trend.net < 0 ? 'green' : 'grey'} isCompact>
                      {trend.net > 0 ? '+' : ''}
                      {trend.net} ({trend.opened} opened, {trend.resolved} resolved)
                    </Label>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </div>
      <p className="risk-register__note">
        Manager mapping is unavailable for {missingManagers} open item{missingManagers === 1 ? '' : 's'};{' '}
        {unattributedApps} cannot be attributed to one application. These are mapping gaps, not inferred ownership.
      </p>
    </>
  );
};
